import { computed, ref } from "vue"
import { axiosToken } from "@/utils/http"
import { useToast } from "primevue/usetoast"
import { useStoreI18n } from "@/composables/useStoreI18n"

export type MapConfigGameType = "ipad_hunt" | "cluedo_ai"

export interface MapConfigItem {
    id: string
    name: string
    created_at?: string
    updated_at?: string
    center?: {
        latitude?: string | number | null
        longitude?: string | number | null
        finish_latitude?: string | number | null
        finish_longitude?: string | number | null
    }
    points?: unknown[]
    routes?: unknown[]
}

const CONTENT_TYPE_BY_GAME: Record<MapConfigGameType, string> = {
    ipad_hunt: "ipad_settings",
    cluedo_ai: "cluedo_ai_settings",
}

const APPLY_ENDPOINT_BY_GAME: Record<MapConfigGameType, string> = {
    ipad_hunt: "IpadHunt/apply_map_config",
    cluedo_ai: "cluedoAi/apply_map_config",
}

const SAVE_ENDPOINT_BY_GAME: Record<MapConfigGameType, string> = {
    ipad_hunt: "IpadHunt/save_map_config",
    cluedo_ai: "cluedoAi/save_map_config",
}

export function useMapConfig(gameType: MapConfigGameType) {
    const { t } = useStoreI18n()
    const toast = useToast()
    const configs = ref<MapConfigItem[]>([])
    const selectedConfigId = ref<string | null>(null)
    const loadingConfigs = ref(false)
    const loadingApply = ref(false)
    const loadingSave = ref(false)

    const contentType = CONTENT_TYPE_BY_GAME[gameType]

    const configOptions = computed(() => [
        { label: t("map_config.none"), value: null },
        ...configs.value.map((config) => ({
            label: config.name,
            value: String(config.id),
        })),
    ])

    const loadConfigOptions = computed(() =>
        configs.value.map((config) => ({
            label: config.name,
            value: String(config.id),
        }))
    )

    const selectedConfig = computed(() =>
        configs.value.find(
            (config) => String(config.id) === String(selectedConfigId.value)
        ) ?? null
    )

    const isConfigLocked = computed(() => Boolean(selectedConfigId.value))

    async function fetchConfigs(templateId: string | number | null | undefined) {
        if (!templateId || templateId === "0" || templateId === 0) {
            configs.value = []
            return []
        }
        loadingConfigs.value = true
        try {
            const response = await axiosToken(
                "get",
                `content/map_configs/${contentType}/${templateId}/`
            )
            configs.value = response.data?.configs ?? []
            return configs.value
        } finally {
            loadingConfigs.value = false
        }
    }

    async function applyConfig(eventApiKey: string, configId: string) {
        loadingApply.value = true
        try {
            const response = await axiosToken(
                "post",
                `${APPLY_ENDPOINT_BY_GAME[gameType]}/${eventApiKey}/`,
                { config_id: configId }
            )
            selectedConfigId.value = String(configId)
            toast.add({
                severity: "success",
                summary: t("map_config.loaded"),
                life: 3000,
            })
            return response.data as MapConfigItem
        } catch {
            toast.add({
                severity: "error",
                summary: t("map_config.load_error"),
                life: 4000,
            })
            throw new Error("apply_failed")
        } finally {
            loadingApply.value = false
        }
    }

    function resolveSaveName(name: string, overwriteId?: string | null) {
        const trimmedName = name.trim()
        if (trimmedName) return trimmedName
        if (!overwriteId) return ""
        const existing = configs.value.find(
            (config) => String(config.id) === String(overwriteId)
        )
        return existing?.name?.trim() ?? ""
    }

    async function saveConfig(
        eventApiKey: string,
        name: string,
        overwriteId?: string | null
    ) {
        const resolvedName = resolveSaveName(name, overwriteId)
        loadingSave.value = true
        try {
            const response = await axiosToken(
                "post",
                `${SAVE_ENDPOINT_BY_GAME[gameType]}/${eventApiKey}/`,
                {
                    name: resolvedName,
                    overwrite_id: overwriteId ?? undefined,
                }
            )
            const saved = response.data as MapConfigItem
            const existingIndex = configs.value.findIndex((config) => config.id === saved.id)
            if (existingIndex >= 0) {
                configs.value[existingIndex] = saved
            } else {
                configs.value.push(saved)
            }
            selectedConfigId.value = saved.id
            toast.add({
                severity: "success",
                summary: t("map_config.saved"),
                life: 3000,
            })
            return saved
        } catch {
            toast.add({
                severity: "error",
                summary: t("map_config.save_error"),
                life: 4000,
            })
            throw new Error("save_failed")
        } finally {
            loadingSave.value = false
        }
    }

    function applyCenterToActivityConfiguration(
        config: MapConfigItem | null,
        activityConfiguration: Record<string, unknown>
    ) {
        if (!config?.center) return
        const center = config.center
        if (center.latitude != null) {
            activityConfiguration.latitude = Number(center.latitude)
        }
        if (center.longitude != null) {
            activityConfiguration.longitude = Number(center.longitude)
        }
        if (center.finish_latitude != null) {
            activityConfiguration.finish_latitude = Number(center.finish_latitude)
        }
        if (center.finish_longitude != null) {
            activityConfiguration.finish_longitude = Number(center.finish_longitude)
        }
    }

    function resolveConfigIdForDialog(
        currentId: string | null | undefined
    ): string | null {
        if (!currentId) return null
        const match = configs.value.find(
            (config) => String(config.id) === String(currentId)
        )
        return match ? String(match.id) : null
    }

    function setSelectedConfigId(configId: string | null) {
        selectedConfigId.value = configId ? String(configId) : null
    }

    return {
        configs,
        selectedConfigId,
        selectedConfig,
        configOptions,
        loadConfigOptions,
        isConfigLocked,
        loadingConfigs,
        loadingApply,
        loadingSave,
        fetchConfigs,
        applyConfig,
        saveConfig,
        applyCenterToActivityConfiguration,
        setSelectedConfigId,
        resolveConfigIdForDialog,
    }
}
